/**
 * ChatSidebar - RAG-based chat interface for querying the knowledge base
 *
 * "Chat with your brain" - Ask questions and get answers based on your notes
 * Now with session management for conversation history
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Brain,
  Sparkles,
  FileText,
  Trash2,
  Plus,
  History,
  Check,
  Lightbulb,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { useMapStore } from '../../stores/mapStore';

const ChatSidebar = () => {
  const {
    isChatOpen,
    toggleChat,
    chatMessages,
    isChatLoading,
    sendChatMessage,
    loadChatSessions,
    chatSessions,
    currentChatSession,
    selectChatSession,
    createChatSession,
    deleteChatSession,
    currentMap,
    suggestedNode,
    acceptSuggestedNode,
    dismissSuggestedNode,
  } = useMapStore();

  const [input, setInput] = useState('');
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load chat sessions when map changes
  useEffect(() => {
    if (currentMap && isChatOpen) {
      loadChatSessions();
    }
  }, [currentMap?.id, isChatOpen, loadChatSessions]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Focus input when sidebar opens
  useEffect(() => {
    if (isChatOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isChatOpen]);

  // Handle send message
  const handleSend = async () => {
    if (!input.trim() || isChatLoading) return;

    const query = input.trim();
    setInput('');

    try {
      await sendChatMessage(query);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={toggleChat}
        className={`fixed right-4 bottom-20 z-40 p-3 rounded-full shadow-lg transition-all duration-200 ${
          isChatOpen
            ? 'bg-indigo-600 text-white'
            : 'bg-gray-800 text-gray-300 hover:bg-gray-750'
        }`}
      >
        <MessageSquare size={20} />
      </button>

      {/* Sidebar */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-gray-900 border-l border-gray-800 shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <Brain size={20} className="text-indigo-400" />
                <h2 className="text-lg font-semibold text-white">
                  Chat with Brain
                </h2>
              </div>
              <div className="flex items-center gap-1">
                {/* New chat button */}
                <button
                  onClick={() => createChatSession()}
                  className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                  title="New chat"
                >
                  <Plus size={18} className="text-gray-400" />
                </button>
                {/* Sessions dropdown */}
                <button
                  onClick={() => setShowSessions(!showSessions)}
                  className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                  title="Chat history"
                >
                  <History size={18} className="text-gray-400" />
                </button>
                <button
                  onClick={toggleChat}
                  className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Sessions dropdown */}
            <AnimatePresence>
              {showSessions && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-b border-gray-800 overflow-hidden"
                >
                  <div className="p-2 max-h-48 overflow-y-auto">
                    {chatSessions.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-2">No chat history</p>
                    ) : (
                      chatSessions.map((session) => (
                        <div
                          key={session.id}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group ${
                            currentChatSession?.id === session.id
                              ? 'bg-indigo-600/20 border border-indigo-500/30'
                              : 'hover:bg-gray-800'
                          }`}
                          onClick={() => {
                            selectChatSession(session.id);
                            setShowSessions(false);
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{session.title}</p>
                            <p className="text-xs text-gray-500">
                              {format(new Date(session.updated_at), 'MMM d, h:mm a')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            {currentChatSession?.id === session.id && (
                              <Check size={14} className="text-indigo-400" />
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Delete this chat session?')) {
                                  deleteChatSession(session.id);
                                }
                              }}
                              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                            >
                              <Trash2 size={14} className="text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Info banner */}
            <div className="px-4 py-2 bg-indigo-500/10 border-b border-indigo-500/20">
              <p className="text-xs text-indigo-300 flex items-center gap-1.5">
                <Sparkles size={12} />
                Ask questions about your notes. Answers are based only on your knowledge base.
              </p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <Brain size={32} className="text-gray-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-300 mb-2">
                    Your Second Brain Awaits
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Ask anything about your notes and ideas. The AI will search through your knowledge base to find answers.
                  </p>
                  <div className="space-y-2 text-left w-full">
                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">
                      Try asking:
                    </p>
                    {[
                      'What are my key thoughts on productivity?',
                      'Summarize my notes about marketing',
                      'What connections exist between my ideas?',
                    ].map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => setInput(suggestion)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-400 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        "{suggestion}"
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                          message.role === 'user'
                            ? 'bg-indigo-600 text-white rounded-br-md'
                            : 'bg-gray-800 text-gray-200 rounded-bl-md'
                        }`}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Brain size={12} className="text-indigo-400" />
                            <span className="text-xs text-indigo-400">AI</span>
                          </div>
                        )}
                        {message.role === 'assistant' ? (
                          <div className="text-sm leading-relaxed prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-strong:text-white prose-code:text-indigo-300 prose-code:bg-gray-700/50 prose-code:px-1 prose-code:rounded">
                            <ReactMarkdown>
                              {message.content}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {message.content}
                          </p>
                        )}

                        {/* Source references */}
                        {message.role === 'assistant' && message.context_node_ids?.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-700/50">
                            <FileText size={10} className="text-gray-500" />
                            <span className="text-[10px] text-gray-500">
                              Based on {message.context_node_ids.length} notes
                            </span>
                          </div>
                        )}

                        <span className="block text-[10px] text-gray-400 mt-1 text-right">
                          {format(new Date(message.created_at), 'h:mm a')}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Loading indicator */}
                  {isChatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 size={14} className="text-indigo-400 animate-spin" />
                          <span className="text-sm text-gray-400">
                            Searching your brain...
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Suggested Node Card */}
                  {suggestedNode && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-3"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <Lightbulb size={16} className="text-amber-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs text-amber-400 font-medium mb-1">
                            Add this as a note?
                          </p>
                          <p className="text-sm text-white font-medium">
                            {suggestedNode.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                            {suggestedNode.content}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={acceptSuggestedNode}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-xs font-medium transition-colors"
                        >
                          <CheckCircle size={14} />
                          Add to Map
                        </button>
                        <button
                          onClick={dismissSuggestedNode}
                          className="px-3 py-1.5 hover:bg-gray-700 text-gray-400 rounded-lg text-xs transition-colors"
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input area */}
            <div className="p-4 border-t border-gray-800">
              <div className="relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask your brain..."
                  rows={1}
                  className="w-full px-4 py-3 pr-12 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none"
                  style={{ minHeight: '48px', maxHeight: '120px' }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isChatLoading}
                  className="absolute right-2 bottom-2 p-2 bg-indigo-600 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-500 transition-colors"
                >
                  {isChatLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatSidebar;
